import React, { useContext, useMemo } from "react";
import { ProviderHubContext } from "@app/projects/network-assessment/provider-hub/contexts/ProviderHubContext";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";
import { ProviderHubViewEnum } from "@app/projects/network-assessment/provider-hub/interfaces";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import { QESButton } from "@app/shared/qes-standards/Button";
import RoleRestricted from "@app/user/RoleRestricted";
import Roles from "@app/user/roles";

/* Centralized component for rendering Provider Hub banner messages */
export const ProviderHubBannerMessages: React.FC = () => {
  const providerHubContext = useContext(ProviderHubContext);
  const featureFlagContext = useContext(FeatureFlagContext);

  const activeBanner = useMemo(() => {

    if (providerHubContext.isProviderHubMissingData) {
      return (
        <QESAlert type="danger" persist={true}>
          An error occurred when processing your provider data sets for Provider Hub. We are working to resolve the issue. Please check back in a few minutes.
        </QESAlert>
      );
    }

    if (providerHubContext.hasFilteredCriteriaFailedToLoad) {
      return (
        <QESAlert type="danger" persist={true}>
          Filter criteria failed to load. Please try refreshing your browser page. If the error persists, please contact Quest Client Services.
        </QESAlert>
      );
    }

    if (providerHubContext.isProviderHubError) {
      return (
        <QESAlert type="danger" persist={true}>
          <strong>Warning!</strong> Provider Hub has encountered an error. Please reach out Quest Client Services for assistance.
          <RoleRestricted allowedRoles={[Roles.RESOURCE_ADMIN, Roles.CLIENT_ADMIN]}>
            <QESButton
              className="ms-3"
              qesType="secondary"
              onClick={providerHubContext.retryIngestion}
              disabled={providerHubContext.isRetryingIngestion}
              qesTestId={"provider-hub-retry-button"}
            >
              Retry
            </QESButton>
          </RoleRestricted>
        </QESAlert>
      );
    }

    if (providerHubContext.isProviderHubProcessing) {
      return (
        <QESAlert type="info" persist={true}>
          Provider Hub is still processing the time slice. If this message persists, please reach out to Quest Client Services for assistance.
        </QESAlert>
      );
    }

    if (providerHubContext.isNiMarketProviderFailed) {
      return (
        <QESAlert type="danger" persist={true}>
          The Network Integrity analysis for Market Providers failed. Results may not be available if reviewing a Market provider.
        </QESAlert>)
    }

    if (providerHubContext.isNiMarketProviderProcessing) {
      return (
        <QESAlert type="info" persist={true}>
          The Network Integrity analysis for Market Providers is still being processed. Results may not be available if reviewing a Market provider.
        </QESAlert>)
    }


    if (!featureFlagContext.enabledFeatureFlags.has(Features.PROVIDER_HUB_PROJECTION)) return null;

    if (providerHubContext.isScenarioModelingProcessing &&
      (!providerHubContext.isProjectReadOnly || providerHubContext.view === ProviderHubViewEnum.ProviderProfile)
    )
    {
      return (
        <QESAlert type="info" persist={true}>
          This project is in read-only mode while processing update. Some actions in Provider Hub may be unavailable.
        </QESAlert>
      );
    }

    if (providerHubContext.isScenarioModelingError)
      return (
        <QESAlert type="danger" persist={true}>
          An error occurred. Provider Actions are unavailable. Please contact Quest Client Services for assistance.
          <RoleRestricted allowedRoles={[Roles.RESOURCE_ADMIN, Roles.CLIENT_ADMIN]}>
            <QESButton
              className="ms-3"
              qesType="secondary"
              onClick={providerHubContext.retryIngestion}
              disabled={providerHubContext.isRetryingIngestion}
              qesTestId={"provider-hub-profile-retry-button"}
            >
              Retry
            </QESButton>
          </RoleRestricted>
        </QESAlert>
      )

    // No banner to display
    return null;
  }, [
    providerHubContext.isProviderHubMissingData,
    providerHubContext.hasFilteredCriteriaFailedToLoad,
    providerHubContext.isProviderHubError,
    providerHubContext.isNiMarketProviderFailed,
    providerHubContext.isNiMarketProviderProcessing,
    providerHubContext.isScenarioModelingError,
    providerHubContext.isProviderHubProcessing,
    providerHubContext.isScenarioModelingProcessing,
    providerHubContext.isRetryingIngestion,
    providerHubContext.retryIngestion,
    featureFlagContext.enabledFeatureFlags
  ]);

  return <>{activeBanner}</>;
};
